import { getSystemTimeZone } from "../units/timezone";

const ISO_DATE_LENGTH = 10;

/** Server timestamps are anchored to a monotonic clock, never to the workstation offset. */
let anchor: { utc: number; tick: number } | undefined;

export const monotonicNow = (): number => performance.now();

export function observeServerTime(headers?: Pick<Headers, "get">): void {
  // Cached assets may retain an old Date header; only dynamic API JSON is a clock source.
  const contentType = headers?.get("Content-Type");
  if (contentType && !contentType.toLowerCase().includes("json")) return;
  if (Number(headers?.get("Age") ?? 0) > 0 || /\b(?:s-maxage|max-age)\s*=/.test(headers?.get("Cache-Control") ?? "")) return;
  const value = headers?.get("Date");
  const utc = value ? Date.parse(value) : NaN;
  if (Number.isFinite(utc)) anchor = { utc, tick: monotonicNow() };
}

/** Before the first response this is only suitable for editable defaults, never authorization. */
export function serverNow(): number {
  return anchor ? anchor.utc + monotonicNow() - anchor.tick : Date.now();
}

export function serverDate(): Date { return new Date(serverNow()); }

export function serverDateOnly(timeZone = getSystemTimeZone()): string {
  return dateOnlyAt(serverDate(), timeZone);
}

export function serverDateOnlyAfterDays(days: number, timeZone = getSystemTimeZone()): string {
  const [year, month, day] = serverDateOnly(timeZone).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, ISO_DATE_LENGTH);
}

export function dateOnlyAt(instant: Date, timeZone: string): string {
  // display-format: Calendar-key calculation, not a formatted UI value.
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(instant);
  const part = (name: Intl.DateTimeFormatPartTypes) => parts.find(value => value.type === name)!.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function resetServerClock(): void { anchor = undefined; }
